import { HttpErrors } from '@omecare/utils';

const BUSINESS_ERROR_TYPE_PATTERN = /^[a-z][a-z0-9_]*$/;

/**
 * Tells whether a value looks like a snake_case business-error type.
 *
 * Business errors carry a snake_case type (e.g. `office_not_found`) whereas
 * generic/framework errors carry a human sentence in the same field (e.g.
 * `Internal server error`, `Not Found`). Only the former maps to an
 * `http.errors.*` translation key, so callers must not treat a human message
 * as a translatable type.
 *
 * @param value - The candidate extracted from the error payload.
 * @returns `true` when the value matches the snake_case type shape.
 */
export const isBusinessErrorType = (value: string): boolean => {
  return BUSINESS_ERROR_TYPE_PATTERN.test(value);
};

/**
 * Extracts the snake_case error type from an API error response.
 *
 * Legacy endpoints carry the type in `data.error`; business errors mapped by
 * the API `ExceptionMapper` carry it in `data.message` (body shape:
 * `{ code, message, timestamp, context }`). Values that are not snake_case
 * business types (framework/unexpected errors carrying a human sentence) yield
 * `false` so callers fall back to the generic handling instead of building a
 * bogus translation key.
 *
 * @param error - The HTTP error thrown by the http service.
 * @returns The snake_case error type, or `false` when none can be extracted.
 */
export const getErrorType = (error: HttpErrors): string | false => {
  if (!error.data) {
    return false;
  }

  let errorType: string | undefined;
  if (typeof error.data.error === 'string') {
    errorType = error.data.error;
    if (errorType === 'Bad Request') {
      errorType = 'bad_request';
    }
  } else if (typeof error.data.message === 'string') {
    errorType = error.data.message;
  }

  if (!errorType || !isBusinessErrorType(errorType)) {
    return false;
  }

  return errorType;
};
